<template>
  <div id="annotation-list">
    <div v-if="annotations.data?.length === 0" class="mt-6 px-4 text-gray-600">
      <p>
        Your organization doesn't have any annotations for the selected period
        yet.
      </p>
      <template v-if="canCreate">
        <br />
        <p>
          You may wait for the system to automatically generate some, or
          <button
            type="button"
            data-annotation-create
            data-tour="annotation-create"
            class="text-blue-500 transition duration-200 hover:text-blue-700 focus:outline-none"
            @click="onClickCreateAnnotation"
          >
            create one yourself.
          </button>
        </p>
      </template>
    </div>
    <div v-else class="relative">
      <DynamicScroller
        ref="dynamicScrollerRef"
        :items="sortedAnnotations"
        :min-item-size="100"
        key-field="pk"
        page-mode
        class="custom-scroll overflow-x-hidden overflow-y-auto"
      >
        <template #default="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[item.type]"
            ><div>
              <AnnotationListItem
                :class="{
                  'bg-purple-50':
                    timeUnit &&
                    timeUnit !== TimeUnit.WEEKDAY &&
                    clickedDate ===
                      DateTime.fromISO(item.start)
                        .startOf(timeUnit)
                        .toISODate(),
                  'opacity-60':
                    clickedDate &&
                    timeUnit &&
                    timeUnit !== TimeUnit.WEEKDAY &&
                    clickedDate !==
                      DateTime.fromISO(item.start)
                        .startOf(timeUnit)
                        .toISODate(),
                }"
                :annotation="item"
              />
            </div>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  toRef,
  watch,
  onBeforeUnmount,
  DeepReadonly,
  ref,
  nextTick,
} from "vue";
import {
  Annotation,
  useAnnotationsForClickedDate,
  useAnnotationsQuery,
} from "@/services/annotation";
import AnnotationListItem from "@/components/Core/Annotations/AnnotationListItem.vue";
import { DateTime } from "luxon";
import * as lodash from "lodash-es";
import { useAnnotationsStore } from "@/store/annotations";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";
import { TimeUnit } from "@/services/data_filters";

defineProps({
  // Off for public/shared-dashboard readers, who can't create annotations.
  canCreate: { type: Boolean, default: false },
});

const emit = defineEmits({ "create-annotation": () => true });

const annotationsStore = useAnnotationsStore();
const timeUnit = toRef(annotationsStore, "timeUnit");
const sortedAnnotations = computed(() => {
  return lodash.orderBy(annotations.data ?? [], ["start"], ["desc"]);
});

annotationsStore.setDate(null, null);
const annotations = useAnnotationsQuery();
const clickedDate = toRef(annotationsStore, "clickedDate");
onBeforeUnmount(() => {
  annotationsStore.setDate(null, null);
});

const dynamicScrollerRef = ref();
function scrollToView(annotation: DeepReadonly<Annotation> | undefined): void {
  if (!annotation) {
    return;
  }
  const index = sortedAnnotations.value.findIndex(
    (annotation2) => annotation2.pk === annotation.pk,
  );
  if (index) {
    nextTick(() => {
      dynamicScrollerRef.value.scrollToItem(index);
    });
  }
}

const annotationsForClickedDate = useAnnotationsForClickedDate();

watch(clickedDate, () => {
  if (annotationsForClickedDate.value.length > 0) {
    const annotation = lodash.maxBy(annotationsForClickedDate.value, "start");
    scrollToView(annotation);
  }
});

function onClickCreateAnnotation(): void {
  emit("create-annotation");
}
</script>
